import type { PrintPayloadOptions, PrintReceiptSource } from '../types'

export type NameValue = { name: string; value: string | boolean }

export interface CustomSections {
  CustomHeaders: NameValue[]
  CustomFooters: NameValue[]
}

/**
 * `CustomHeaders` / `CustomFooters` are a flat opcode stream the print service
 * interprets by row NAME (PrintTitle, PrintComment, LineFeed, PrintLine,
 * PrintTextLine, ...); an unrecognised name renders as a key/value line.
 * See PrintServiceV2 Graphic/HelperDrawFromNameValues.cs.
 *
 * Always built here and never read from the record: the stored value has always
 * been `[]` — the builder overwrote it on every print.
 */

/** True when PaymentType carries a JSON array of split payments rather than a plain name. */
export const isMultiplePayment = (payments: any): boolean => {
  if (!payments) return false
  if (typeof payments === 'string') {
    try {
      JSON.parse(payments)
      return true
    } catch {
      return false
    }
  }
  return true
}

export const buildCustomSections = (
  source: PrintReceiptSource,
  options: PrintPayloadOptions,
): CustomSections & { PaymentType?: string } => {
  const CustomHeaders: NameValue[] = []
  const CustomFooters: NameValue[] = []
  const result: CustomSections & { PaymentType?: string } = { CustomHeaders, CustomFooters }

  if (options?.isCopy) {
    const label = options?.isPrintAll
      ? '**** COPY RECEIPT (PRINT ALL) ****'
      : '**** COPY RECEIPT ****'
    const copyText: NameValue = { name: 'PrintComment', value: label }
    CustomHeaders.push(copyText)
    CustomFooters.push(copyText)
  }

  if (isMultiplePayment(source?.PaymentType)) {
    const paymentTypes = JSON.parse(source.PaymentType)
    CustomFooters.push(
      { name: 'PrintTextLine', value: 'Payment Detail ' },
      ...paymentTypes.map((payment: any) => ({
        name: 'PrintTextLine',
        value: `     ${payment.name} : ${payment.amount}`,
      })),
      { name: 'LineFeed', value: true },
    )
    result.PaymentType = 'Multiple Payment'
  }

  return result
}
